import React, { useState } from "react";
import "./Contact.css";
import { contactAPI } from "../services/api";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useRevealOnIntersect(".hc-reveal", { threshold: 0.2 });

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Store form reference before async operation
    const form = e.currentTarget;

    try {
      const formData = Object.fromEntries(new FormData(form).entries());
      const result = await contactAPI.submitContactForm(formData);
      
      if (result.success || result.fallback) {
        setSent(true);
        // Use stored form reference
        if (form) {
          form.reset();
        }
        
        // Show different message for fallback
        if (result.fallback) {
          setTimeout(() => setSent(false), 15000); // Longer for fallback message
        } else {
          setTimeout(() => setSent(false), 8000);
        }
      } else {
        setError(result.message || 'Failed to send message. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setError(error.message || 'Failed to send message. Please contact us directly at info.nexite@gmail.com or call +92 323 5522788.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section id="contact" className="home-contact" aria-label="Contact Nexite">
      {/* Ambient background */}
      <div className="hc-bg" aria-hidden="true">
        <span className="hc-blob b1" />
        <span className="hc-blob b2" />
        <span className="hc-blob b3" />
        <span className="hc-grid-glow" />
      </div>

      <div className="hc-container">
        <header className="hc-header hc-reveal" data-reveal="up" style={{ "--d": "40ms" }}>
          <span className="hc-pill">Let’s talk</span>
          <h2>Contact Us</h2>
          <p className="hc-subtitle">
            Tell us about your goals. We’ll reply with a clear, actionable plan within one business day.
          </p>
        </header>

        {/* Quick contact tiles */}
        <div className="hc-tiles">
          <a
            className="hc-tile hc-reveal"
            data-reveal="up"
            href="tel:+923235522788"
            aria-label="Call Nexite at +92 323 5522788"
          >
            <div className="hc-tile-icon">📞</div>
            <div className="hc-tile-body">
              <strong>Call</strong>
              <span>+92 323 5522788</span>
            </div>
            <span className="tile-shine" />
          </a>

          <a
            className="hc-tile hc-reveal"
            data-reveal="up"
            style={{ "--d": "80ms" }}
            href="mailto:info.nexite@gmail.com"
            aria-label="Email info.nexite@gmail.com"
          >
            <div className="hc-tile-icon">✉️</div>
            <div className="hc-tile-body">
              <strong>Email</strong>
              <span>info.nexite@gmail.com</span>
            </div>
            <span className="tile-shine" />
          </a>

          <a
            className="hc-tile hc-reveal"
            data-reveal="up"
            style={{ "--d": "160ms" }}
            href="https://maps.google.com/?q=Gulberg Greens, Islamabad"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Nexite address on Google Maps"
          >
            <div className="hc-tile-icon">📍</div>
            <div className="hc-tile-body">
              <strong>Visit</strong>
              <span>Gulberg Greens, Islamabad</span>
            </div>
            <span className="tile-shine" />
          </a>
        </div>

        {/* Main grid */}
        <div className="hc-grid">
          {/* Form */}
          <article className="hc-card hc-reveal" data-reveal="left">
            <h3>Send us a message</h3>
            <p className="hc-muted">We usually respond within one business day.</p>

            {sent && (
              <div className="hc-alert success" role="status" aria-live="polite">
                <span>✓</span>
                <div>
                  <strong>Message sent.</strong> Thank you! We'll be in touch shortly.
                </div>
              </div>
            )}

            {error && (
              <div className="hc-alert error" role="alert" aria-live="assertive">
                <span>⚠</span>
                <div>
                  <strong>Error:</strong> {error}
                </div>
              </div>
            )}

            <form className="hc-form" onSubmit={handleSubmit} noValidate>
              <div className="hc-row">
                <div className="hc-field">
                  <input id="hc_name" name="name" type="text" placeholder=" " required />
                  <label htmlFor="hc_name">Your Name</label>
                </div>
                <div className="hc-field">
                  <input id="hc_email" name="email" type="email" placeholder=" " required />
                  <label htmlFor="hc_email">Your Email</label>
                </div>
              </div>

              <div className="hc-row">
                <div className="hc-field">
                  <input id="hc_phone" name="phone" type="tel" placeholder=" " />
                  <label htmlFor="hc_phone">Phone (optional)</label>
                </div>
                <div className="hc-field">
                  <select id="hc_topic" name="topic" defaultValue="General Inquiry">
                    <option>General Inquiry</option>
                    <option>Security</option>
                    <option>Network</option>
                    <option>IT Infrastructure</option>
                    <option>General Order Supply</option>
                    <option>Partnership</option>
                  </select>
                  <label htmlFor="hc_topic">Topic</label>
                </div>
              </div>

              <div className="hc-field">
                <input id="hc_subject" name="subject" type="text" placeholder=" " required />
                <label htmlFor="hc_subject">Subject</label>
              </div>

              <div className="hc-field">
                <textarea id="hc_message" name="message" rows="6" placeholder=" " required />
                <label htmlFor="hc_message">Your Message</label>
              </div>

              <label className="hc-check">
                <input type="checkbox" name="consent" required />
                <span>I agree to be contacted and accept the privacy policy.</span>
              </label>

              <button type="submit" className="hc-btn hc-btn-primary" disabled={isSubmitting}>
                <span className="shine" aria-hidden="true" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </article>

          {/* Side info */}
          <aside className="hc-side hc-reveal" data-reveal="right">
            <div className="hc-card mini">
              <h4>Talk to Sales</h4>
              <p>Planning a deployment or modernization? We’ll recommend the best path forward.</p>
              <a className="hc-link" href="tel:+923235522788">Call +92 323 5522788</a>
            </div>

            <div className="hc-card mini">
              <h4>Support & SLAs</h4>
              <p>Existing customer? Include your site and ticket reference for faster help.</p>
              <a className="hc-link" href="mailto:info.nexite@gmail.com?subject=Support%20Request">Email Support</a>
            </div>

            <div className="hc-card map">
              <div className="hc-map">
                <div className="hc-map-title">Our Location</div>
                <p>Gulberg Greens, Islamabad</p>
                <a
                  className="hc-btn hc-btn-secondary"
                  href="https://maps.google.com/?q=Gulberg Greens, Islamabad"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Maps
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="hc-accent" aria-hidden="true" />
    </section>
  );
}